import { cn } from '@/utils/cn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { EmptyState } from '@/components/ui/EmptyState';
import { TableSkeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { TABLE_CELL_PADDING, type TableDensity } from '@/design-system/tokens';
import { formatTableCellValue } from '@/utils/tableCellValue';
import {
  dataTablePageProps,
  DEFAULT_PAGE_SIZE,
  paginateClientList,
  usePagination,
} from '@/hooks/usePagination';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T | ((row: T) => string | number);
  loading?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  page?: number;
  totalPages?: number;
  totalElements?: number;
  onPageChange?: (page: number) => void;
  actions?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** @deprecated Use emptyTitle */
  emptyMessage?: string;
  onExport?: () => void;
  exportLabel?: string;
  bulkActions?: ReactNode;
  selectedCount?: number;
  density?: TableDensity;
  stickyHeader?: boolean;
  /** Omit outer card shell when already inside a card/section */
  embedded?: boolean;
  /** Page size when using automatic client-side pagination (no onPageChange). */
  pageSize?: number;
}

export function DataTable<T extends object>({
  columns,
  data,
  keyField,
  loading,
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  page = 0,
  totalPages = 0,
  totalElements,
  onPageChange,
  actions,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  emptyMessage = 'No records found',
  onExport,
  exportLabel = 'Export',
  bulkActions,
  selectedCount = 0,
  density = 'comfortable',
  stickyHeader = true,
  embedded = false,
  pageSize = DEFAULT_PAGE_SIZE,
}: DataTableProps<T>) {
  const clientPagination = usePagination(pageSize);
  const isClientPaged = onPageChange === undefined;

  useEffect(() => {
    if (!isClientPaged) return;
    clientPagination.applyClientCount(data.length);
  }, [isClientPaged, data.length, clientPagination.applyClientCount]);

  useEffect(() => {
    if (!isClientPaged || clientPagination.totalPages === 0) return;
    if (clientPagination.page >= clientPagination.totalPages) {
      clientPagination.resetPage();
    }
  }, [
    isClientPaged,
    clientPagination.page,
    clientPagination.totalPages,
    clientPagination.resetPage,
  ]);

  const clientPageProps = isClientPaged ? dataTablePageProps(clientPagination) : null;
  const displayData = isClientPaged
    ? paginateClientList(data, clientPagination.page, clientPagination.size)
    : data;
  const resolvedPage = clientPageProps?.page ?? page;
  const resolvedTotalPages = clientPageProps?.totalPages ?? totalPages;
  const resolvedTotalElements = clientPageProps?.totalElements ?? totalElements;
  const resolvedOnPageChange = clientPageProps?.onPageChange ?? onPageChange;

  const cellPad = TABLE_CELL_PADDING[density];

  const getKey = (row: T, index: number) => {
    if (typeof keyField === 'function') return keyField(row);
    return String(row[keyField as keyof T] ?? index);
  };

  const title = emptyTitle ?? emptyMessage;
  const showToolbar = onSearchChange || actions || onExport || bulkActions;

  return (
    <div className={cn(embedded ? 'overflow-visible' : 'card overflow-visible')}>
      {showToolbar && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3 dark:border-slate-800">
          {onSearchChange && (
            <div className="relative min-w-[200px] max-w-md flex-1">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search ?? ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="input pl-9"
                aria-label="Search table"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {selectedCount > 0 && bulkActions}
            {onExport && (
              <Button type="button" variant="secondary" size="sm" onClick={onExport}>
                <FileDownloadIcon fontSize="small" />
                {exportLabel}
              </Button>
            )}
            {actions}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={6} cols={Math.min(columns.length, 5)} />
          </div>
        ) : displayData.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title={title}
              description={emptyDescription}
              actionLabel={emptyActionLabel}
              onAction={onEmptyAction}
            />
          </div>
        ) : (
          <table className={cn('w-full text-left text-sm', stickyHeader && 'table-sticky-head')}>
            <thead className="bg-slate-50/95 backdrop-blur-sm dark:bg-slate-900/95">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      cellPad.th,
                      'text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400',
                      col.className,
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, i) => (
                <tr
                  key={getKey(row, i)}
                  className="border-t border-slate-100 transition-colors hover:bg-slate-50/80 dark:border-slate-800 dark:hover:bg-slate-800/40"
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn(cellPad.td, 'text-slate-700 dark:text-slate-300', col.className)}>
                      {col.render
                        ? col.render(row)
                        : formatTableCellValue((row as Record<string, unknown>)[col.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {resolvedOnPageChange && (
        <TablePagination
          page={resolvedPage}
          totalPages={resolvedTotalPages}
          totalElements={resolvedTotalElements}
          onPageChange={resolvedOnPageChange}
          loading={loading}
          hasRows={data.length > 0}
        />
      )}
    </div>
  );
}
